import { errorFilledToastOptions, ibanLengths } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { PaymentMethod } from "../../../../types"
import { toast } from "react-toastify"
import { CountryType, isClabeValid, isJapaneseAccountHolderName } from "./countryConfig"

const checkIbanValidity = (iban: string) => {
  const cleanedIban = iban.replace(/\s+/g, "")

  if (cleanedIban.length < 2) return lang.account.payment_methods.payment_form.error.short_iban

  const firstTwo = cleanedIban.slice(0, 2)

  const allowedLength = ibanLengths[firstTwo]

  if (!allowedLength) return lang.account.payment_methods.payment_form.error.country_code_invalid

  if (allowedLength > cleanedIban.length) return lang.account.payment_methods.payment_form.error.short_iban

  if (allowedLength < cleanedIban.length) return lang.account.payment_methods.payment_form.error.long_iban

  const rearranged = cleanedIban.slice(4) + cleanedIban.slice(0, 4)

  let numericIban = ""
  for (const char of rearranged) {
    const code = char.charCodeAt(0)
    numericIban += code >= 65 && code <= 90 ? code - 55 : char
  }
  let remainder = numericIban
  while (remainder.length > 2) {
    const block = remainder.slice(0, 9)
    remainder = (parseInt(block, 10) % 97) + remainder.slice(block.length)
  }

  if (!(parseInt(remainder, 10) % 97 === 1)) return lang.account.payment_methods.payment_form.error.invalid_iban

  return null
}

const paymentMethodValidator = (formValues: PaymentMethod, countryType: CountryType) => {
  const errors: any = {}
  const err = lang.account.payment_methods.payment_form.error

  if (!formValues.accountHolderName) {
    errors.accountHolderName = err.account_holder_name_blank
    toast.error(err.account_holder_name_blank, errorFilledToastOptions)
  }

  if (!formValues.currency) {
    errors.currency = err.currency_select
    toast.error(err.currency_select, errorFilledToastOptions)
  }

  switch (countryType) {
    case "us": {
      const routingClean = (formValues.bankCode || "").replace(/\D/g, "")
      if (routingClean.length !== 9) {
        errors.bankCode = err.routing_number_invalid
        toast.error(err.routing_number_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "ng": {
      const nuban = (formValues.accountNumber || "").replace(/\D/g, "")
      if (nuban.length !== 10) {
        errors.accountNumber = err.nuban_invalid
        toast.error(err.nuban_invalid, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.select_bank
        toast.error(err.select_bank, errorFilledToastOptions)
      }
      break
    }

    case "za": {
      const branchCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (branchCode.length !== 6) {
        errors.bankCode = err.branch_code_invalid
        toast.error(err.branch_code_invalid, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.select_bank
        toast.error(err.select_bank, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      if (!formValues.accountType) {
        errors.accountType = err.account_type_required
        toast.error(err.account_type_required, errorFilledToastOptions)
      }
      break
    }

    case "au": {
      const bsb = (formValues.bankCode || "").replace(/\D/g, "")
      if (bsb.length !== 6) {
        errors.bankCode = err.bsb_invalid
        toast.error(err.bsb_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "ca": {
      const cleanBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (!cleanBankCode || cleanBankCode.length !== 8) {
        errors.bankCode = err.transit_institution_invalid
        toast.error(err.transit_institution_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "in": {
      const ifsc = (formValues.bankCode || "").toUpperCase()
      if (!ifsc || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
        errors.bankCode = err.ifsc_invalid
        toast.error(err.ifsc_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "sg": {
      const cleanBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (!cleanBankCode || cleanBankCode.length !== 7) {
        errors.bankCode = err.bank_branch_invalid
        toast.error(err.bank_branch_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "br": {
      const cleanBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (!cleanBankCode || cleanBankCode.length < 7 || cleanBankCode.length > 8) {
        errors.bankCode = err.bank_code_agency_invalid
        toast.error(err.bank_code_agency_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      if (!formValues.accountType) {
        errors.accountType = err.account_type_required
        toast.error(err.account_type_required, errorFilledToastOptions)
      }
      const taxId = (formValues.taxId || "").replace(/\D/g, "")
      if (!taxId || (taxId.length !== 11 && taxId.length !== 14)) {
        errors.taxId = err.cpf_cnpj_invalid
        toast.error(err.cpf_cnpj_invalid, errorFilledToastOptions)
      }
      break
    }

    case "ph": {
      if (!formValues.bankName) {
        errors.bankName = err.select_bank
        toast.error(err.select_bank, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "nz": {
      const cleanAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (!cleanAccount || cleanAccount.length < 15 || cleanAccount.length > 16) {
        errors.accountNumber = err.nz_account_invalid
        toast.error(err.nz_account_invalid, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      const swift = (formValues.swiftCode || "").toUpperCase().trim()
      if (!swift) {
        errors.swiftCode = err.swift_bic_required
        toast.error(err.swift_bic_required, errorFilledToastOptions)
      } else if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swift)) {
        errors.swiftCode = err.swift_bic_invalid
        toast.error(err.swift_bic_invalid, errorFilledToastOptions)
      }
      break
    }

    // --- Local formats added for the Asia-Pacific and Latin America corridors ---
    //
    // Each of these countries names its own parts of an account, so the checks are the country's own:
    // a length the bank prints, a check digit the number carries, or an alphabet the receiving bank
    // accepts. The stored shape is the one the older countries already use, a bank code and a branch
    // code joined into bankCode, so nothing downstream has to learn a new field.

    case "jp": {
      const jpBankCode = (formValues.bankCode || "").split("-")[0] || ""
      const jpBranchCode = (formValues.bankCode || "").split("-")[1] || ""
      if (jpBankCode.length !== 4) {
        errors.bankCode = err.jp_bank_code_invalid
        toast.error(err.jp_bank_code_invalid, errorFilledToastOptions)
      }
      if (jpBranchCode.length !== 3) {
        errors.branchCode = err.jp_branch_code_invalid
        toast.error(err.jp_branch_code_invalid, errorFilledToastOptions)
      }
      // The account number is padded to seven on the way out, so what is checked here is what the
      // artist typed: anything from one digit to seven, and never eight.
      const jpAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (jpAccount.length < 1 || jpAccount.length > 7) {
        errors.accountNumber = err.jp_account_invalid
        toast.error(err.jp_account_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountType) {
        errors.accountType = err.account_type_required
        toast.error(err.account_type_required, errorFilledToastOptions)
      }
      // A Japanese bank rejects a holder name written in Kanji or Hiragana rather than transliterating
      // it, so the name is checked here and not left for the payout to fail on.
      if (formValues.accountHolderName && !isJapaneseAccountHolderName(formValues.accountHolderName)) {
        errors.accountHolderName = err.jp_account_holder_name_invalid
        toast.error(err.jp_account_holder_name_invalid, errorFilledToastOptions)
      }
      break
    }

    case "mx": {
      if (!isClabeValid(formValues.accountNumber)) {
        errors.accountNumber = err.clabe_invalid
        toast.error(err.clabe_invalid, errorFilledToastOptions)
      }
      break
    }

    case "kr": {
      const krBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (krBankCode.length < 2 || krBankCode.length > 3) {
        errors.bankCode = err.kr_bank_code_invalid
        toast.error(err.kr_bank_code_invalid, errorFilledToastOptions)
      }
      const krAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (krAccount.length < 10 || krAccount.length > 14) {
        errors.accountNumber = err.kr_account_invalid
        toast.error(err.kr_account_invalid, errorFilledToastOptions)
      }
      break
    }

    case "hk": {
      const hkBankCode = (formValues.bankCode || "").split("-")[0] || ""
      const hkBranchCode = (formValues.bankCode || "").split("-")[1] || ""
      if (hkBankCode.length !== 3) {
        errors.bankCode = err.hk_bank_code_invalid
        toast.error(err.hk_bank_code_invalid, errorFilledToastOptions)
      }
      if (hkBranchCode.length !== 3) {
        errors.branchCode = err.hk_branch_code_invalid
        toast.error(err.hk_branch_code_invalid, errorFilledToastOptions)
      }
      const hkAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (hkAccount.length < 6 || hkAccount.length > 9) {
        errors.accountNumber = err.hk_account_invalid
        toast.error(err.hk_account_invalid, errorFilledToastOptions)
      }
      break
    }

    case "my": {
      // Malaysia takes either identifier in the same box: a short local bank code or a full SWIFT-BIC.
      const myBankCode = (formValues.bankCode || "").toUpperCase().trim()
      const isLocalCode = /^\d{2,4}$/.test(myBankCode)
      const isBic = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(myBankCode)
      if (!isLocalCode && !isBic) {
        errors.bankCode = err.my_bank_code_invalid
        toast.error(err.my_bank_code_invalid, errorFilledToastOptions)
      }
      const myAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (myAccount.length < 9 || myAccount.length > 14) {
        errors.accountNumber = err.my_account_invalid
        toast.error(err.my_account_invalid, errorFilledToastOptions)
      }
      // The ID or passport number is what Malaysia asks for on a payment to a person, and every
      // counterparty Kahuna creates is a personal one, so it is required rather than optional here.
      if (!formValues.taxId) {
        errors.taxId = err.my_id_passport_required
        toast.error(err.my_id_passport_required, errorFilledToastOptions)
      }
      break
    }

    case "id": {
      const idBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (idBankCode.length !== 3) {
        errors.bankCode = err.id_bank_code_invalid
        toast.error(err.id_bank_code_invalid, errorFilledToastOptions)
      }
      const idAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (idAccount.length < 10 || idAccount.length > 15) {
        errors.accountNumber = err.id_account_invalid
        toast.error(err.id_account_invalid, errorFilledToastOptions)
      }
      break
    }

    case "th": {
      const thBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (thBankCode.length !== 3) {
        errors.bankCode = err.th_bank_code_invalid
        toast.error(err.th_bank_code_invalid, errorFilledToastOptions)
      }
      const thAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (thAccount.length !== 10) {
        errors.accountNumber = err.th_account_invalid
        toast.error(err.th_account_invalid, errorFilledToastOptions)
      }
      break
    }

    case "vn": {
      const vnBankCode = (formValues.bankCode || "").toUpperCase().trim()
      if (!/^[A-Z0-9]{3,8}$/.test(vnBankCode)) {
        errors.bankCode = err.vn_bank_code_invalid
        toast.error(err.vn_bank_code_invalid, errorFilledToastOptions)
      }
      const vnAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (vnAccount.length < 8 || vnAccount.length > 15) {
        errors.accountNumber = err.vn_account_invalid
        toast.error(err.vn_account_invalid, errorFilledToastOptions)
      }
      // A state bank in Vietnam routes on the branch, so a payout without one does not arrive.
      if (!formValues.branchName) {
        errors.branchName = err.branch_name_required
        toast.error(err.branch_name_required, errorFilledToastOptions)
      }
      break
    }

    case "uk": {
      const sortCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (sortCode.length !== 6) {
        errors.bankCode = err.sort_code_invalid
        toast.error(err.sort_code_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber || formValues.accountNumber.length !== 8) {
        errors.accountNumber = err.account_number_8_digits
        toast.error(err.account_number_8_digits, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      break
    }

    case "tr": {
      if (!formValues.iban) {
        errors.iban = err.iban_blank
        toast.error(err.iban_blank, errorFilledToastOptions)
      } else {
        const errorMessage = checkIbanValidity(formValues.iban)
        if (errorMessage !== null) {
          errors.iban = errorMessage
          toast.error(errorMessage, errorFilledToastOptions)
        }
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      break
    }

    case "eu":
    default: {
      if (!formValues.iban) {
        errors.iban = err.iban_blank
        toast.error(err.iban_blank, errorFilledToastOptions)
      } else {
        const errorMessage = checkIbanValidity(formValues.iban)
        if (errorMessage !== null) {
          errors.iban = errorMessage
          toast.error(errorMessage, errorFilledToastOptions)
        }
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      break
    }
  }

  return errors
}

export default paymentMethodValidator
